import { useEffect, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { LoadingSpinner } from 'src/consumer/components/Loaders'
import { ROLE_TYPES } from 'src/consumer/constants'
import { getData } from 'src/consumer/services/profileService'
import { ClinicDataProvider } from 'src/context/ClinicDataContext'
import { isCommonResponseSuccessful } from 'src/mentor/helpers/utilityFunctions'
import { LayoutContainer } from 'src/mentor/layout/Styles'
import { getAllRolesInBoolean } from 'src/mentor/layout/utils'
import { LayoutChildComponent } from '.'
import ClinicLayoutProvider from '../context/ClinicLayoutProvider'
import { getCPDataWithoutParams } from '../services'
import { getClinicAppointmentsForThisClinicForCA } from './util'

const availableRoles = getAllRolesInBoolean()

export default function CALayout() {
  const navigate = useNavigate()
  const [loading, setLoading] = useState(false)
  const [userProfileData, setUserProfileData] = useState()
  const [associatedClinicsData, setAssociatedClinicsData] = useState([])
  const [associatedClinicChambersData, setAssociatedClinicChambersData] = useState([])
  const [clinicAssistantProfileData, setClinicAssistantProfileData] = useState([])
  const [associatedChamberRoomsData, setAssociatedChamberRoomsData] = useState([])
  const [associatedScreenDeviceConfigs, setAssociatedScreenDeviceConfigs] = useState([])
  const [clinicAppointmentTypes, setClinicAppointmentTypes] = useState()

  async function getAndSetClinicChambersData(clinicArray) {
    // Use Promise.all with map instead of forEach for proper async handling
    const promises = clinicArray.map(async (clinicItem) => {
      const resp = await getCPDataWithoutParams(
        `/api/secure/clinic-assistant/associated/clinic-chamber/list?clinicId=${clinicItem?.id}`,
      )
      if (resp?.status === 200) {
        return resp?.data || []
      }
      return []
    })
    const results = await Promise.all(promises)
    setAssociatedClinicChambersData(results.flat())
  }

  async function getAndSetClinicAppointmentTypesData(clinicArray) {
    // Use Promise.all with map instead of forEach for proper async handling
    const promises = clinicArray
      .filter((clinicItem) => clinicItem?.useClinicAppointmentTypesForBooking)
      .map(async (clinicItem) => {
        const clinicAppointmentsForThisClinic = await getClinicAppointmentsForThisClinicForCA(
          clinicItem?.id,
          clinicItem?.clinicBrandId,
        )
        if (clinicAppointmentsForThisClinic) {
          return { clinicId: clinicItem?.id, data: clinicAppointmentsForThisClinic }
        }
        return null
      })
    const results = await Promise.all(promises)
    const appointmentTypesMap = {}
    results.filter(Boolean).forEach(({ clinicId, data }) => {
      appointmentTypesMap[clinicId] = data
    })
    setClinicAppointmentTypes(appointmentTypesMap)
  }

  async function getAndSetAssociatedScreenDeviceConfigs() {
    const resp = await getCPDataWithoutParams(
      `/api/secure/clinic-assistant/associated/screen-device-configs`,
    )
    if (resp?.status === 200 && isCommonResponseSuccessful(resp?.data?.code)) {
      setAssociatedScreenDeviceConfigs(resp?.data?.data)
    }
  }

  async function getAndSetChamberRoomsData(clinicArray) {
    // Use Promise.all with map instead of forEach for proper async handling
    const promises = clinicArray.map(async (clinicItem) => {
      const resp = await getCPDataWithoutParams(
        `/api/secure/clinic-assistant/associated/chamber-room/list?clinicId=${clinicItem?.id}`,
      )
      if (resp?.status === 200) {
        return resp?.data || []
      }
      return []
    })
    const results = await Promise.all(promises)
    setAssociatedChamberRoomsData(results.flat())
  }

  async function getAndSetAssistantProfileData(clinicArray) {
    // Use Promise.all with map instead of forEach for proper async handling
    const promises = clinicArray.map(async (clinicItem) => {
      const response = await getCPDataWithoutParams(
        `/api/secure/clinic-assistant/profile?clinicId=${clinicItem?.id}`,
      )
      if (response.status === 200) {
        return response?.data?.data?.assistant
      }
      return null
    })
    const results = await Promise.all(promises)
    setClinicAssistantProfileData(results.filter(Boolean))
  }

  async function setDataForCA() {
    // Determine which portal we're in based on current path
    const currentPath = window.location.pathname
    const isKioskPortal = currentPath.startsWith('/kiosk')
    
    // Update lastChosenFlow based on current portal (for existing running sessions)
    // This ensures users already in a portal have their preference set correctly
    if (isKioskPortal) {
      localStorage.setItem('lastChosenFlow', 'kiosk')
    } else {
      // Clinic Assistant portal
      localStorage.setItem('lastChosenFlow', 'clinic-assistant')
    }
    
    // Separate validation for each portal
    if (isKioskPortal) {
      // Kiosk portal - only allow ROLE_AUMHUM_SCREEN
      if (!availableRoles?.isAumhumScreen) {
        navigate('/aumhum-for-clinics')
        return
      }
    } else {
      // Clinic Assistant portal - only allow ROLE_CLINIC_ASSISTANT
      if (!availableRoles?.isClinicAssistant) {
        navigate('/aumhum-for-clinics')
        return
      }
    }
    
    // If we reach here, user has the correct role for the portal
    if (availableRoles?.isClinicAssistant || availableRoles?.isAumhumScreen) {
      setLoading(true)
      try {
        // Parallelize the first two critical API calls
        const [respONE, respTWO] = await Promise.all([
          getData('/api/secure/user/profile'),
          getData('/api/secure/clinic-assistant/associated/clinic-profile/list'),
        ])
        
        if (respONE?.status === 200) {
          setUserProfileData(respONE.data)
          localStorage.setItem('authorities', JSON.stringify(respONE?.data?.authorities))
        }
        
        if (respTWO?.status === 200) {
          setAssociatedClinicsData(respTWO?.data)
        }
        
        // Hide loader early - show UI with basic data, load additional data in background
        setLoading(false)
        
        // Load additional data in parallel after UI is shown (non-blocking)
        if (Array.isArray(respTWO?.data) && respTWO?.data?.length > 0) {
          // Don't await - let these load in background
          Promise.all([
            getAndSetClinicChambersData(respTWO?.data),
            getAndSetChamberRoomsData(respTWO?.data),
            getAndSetAssistantProfileData(respTWO?.data),
            getAndSetAssociatedScreenDeviceConfigs(),
            getAndSetClinicAppointmentTypesData(respTWO?.data),
          ]).catch((error) => {
            console.error('Error loading additional CA data:', error)
          })
        }
      } catch (error) {
        // Handle any errors from the data fetching here
        console.error('Error loading CA data:', error)
        setLoading(false)
      }
    } else {
      navigate('/aumhum-for-clinics')
    }
  }

  useEffect(() => {
    setDataForCA()
  }, [])

  if (loading) {
    return <LoadingSpinner bgColor="#F5F5F599" dark />
  } else {
    return (
      <LayoutContainer>
        <ClinicLayoutProvider>
          <ClinicDataProvider
            chosenRoleFlow={ROLE_TYPES.roleClinicAssistant}
            assistantData={{
              userProfileData,
              associatedClinicsData,
              associatedClinicChambersData,
              clinicAssistantProfileData,
              associatedChamberRoomsData,
              associatedScreenDeviceConfigs,
              associatedClinicAppointmentTypesData: clinicAppointmentTypes,
            }}
          >
            <LayoutChildComponent />
          </ClinicDataProvider>
        </ClinicLayoutProvider>
      </LayoutContainer>
    )
  }
}
